import $ from 'jquery';
import { __, _x } from '@wordpress/i18n';
import { InventorySearchEvents } from '@dealersites/inventory/events';

/**
 * SRP vehicle card image gallery.
 *
 * Builds a small gallery inside each SRP card's image from the photo URLs
 * output by the vehicleGalleryPhotos macro (views/utility/macros/vehicle.twig).
 * Works on both card markups:
 *  - original: .vehicle-card > .featured-image > .vehicle-image (a or img)
 *  - atomic: .vehicle > .vehicle__image > img.vehicle__default-image
 *
 * The gallery has:
 *  - prev/next arrows, revealed on hover (hidden on touch devices, where a tap
 *    still opens the VDP)
 *  - image count dots, styled like the VDP gallery dots
 *  - on the last image the next arrow turns brand colour with a "View" label
 *    and links through to the VDP
 *  - swipe left/right on touch devices
 *
 * Extra images are only requested once the user interacts with a card.
 *
 * Event handlers are delegated and all state lives on the DOM, so cards that
 * the SRP re-renders keep working.
 */

const MAX_PHOTOS = 6;
const SWIPE_THRESHOLD = 40;
const INTERACTING_TIMEOUT = 1500;

const LABELS = {
  prev: __('Previous photo', 'ford-oem-theme'),
  next: __('Next photo', 'ford-oem-theme'),
  view: __('View vehicle', 'ford-oem-theme'),
  viewShort: _x('View', 'vehicle card gallery', 'ford-oem-theme'),
};

const interactingTimers = new WeakMap();

const chevron = (direction) =>
  `<svg class="vehicle-gallery__icon" viewBox="0 0 24 24" aria-hidden="true"><path d="${
    direction === 'left'
      ? 'M15.4 7.4 14 6l-6 6 6 6 1.4-1.4-4.6-4.6z'
      : 'M8.6 16.6 10 18l6-6-6-6-1.4 1.4 4.6 4.6z'
  }"/></svg>`;

function loadSlide(gallery, index) {
  const slide = gallery.querySelectorAll('.vehicle-gallery__slide')[index];
  const img = slide && slide.querySelector('img[data-src]');
  if (!img) return;

  img.addEventListener('load', () => slide.classList.add('is-loaded'), { once: true });
  img.addEventListener('error', () => slide.classList.add('is-error'), { once: true });
  img.src = img.dataset.src;
  img.removeAttribute('data-src');
}

// Flags the gallery while the user is moving through it (see .is-interacting).
function markInteracting(gallery) {
  gallery.classList.add('is-interacting');
  clearTimeout(interactingTimers.get(gallery));
  interactingTimers.set(
    gallery,
    setTimeout(() => gallery.classList.remove('is-interacting'), INTERACTING_TIMEOUT),
  );
}

function goTo(gallery, index) {
  const total = parseInt(gallery.dataset.total, 10);
  const current = Math.max(0, Math.min(index, total - 1));
  const isLast = current === total - 1;

  gallery.dataset.index = current;
  gallery.style.setProperty('--gallery-index', current);
  gallery.classList.toggle('is-first', current === 0);
  gallery.classList.toggle('is-last', isLast);
  const prev = gallery.querySelector('.vehicle-gallery__arrow--prev');
  const next = gallery.querySelector('.vehicle-gallery__arrow--next');
  next.setAttribute('aria-label', isLast ? LABELS.view : LABELS.next);

  // The hidden prev arrow on the first image can't take keyboard focus.
  if (current === 0 && document.activeElement === prev) next.focus();
  prev.disabled = current === 0;

  gallery.querySelectorAll('.vehicle-gallery__dot').forEach((dot, i) => {
    dot.classList.toggle('is-active', i === current);
  });
  markInteracting(gallery);

  // Load the current image and preload the next one.
  loadSlide(gallery, current);
  loadSlide(gallery, current + 1);
}

function buildGallery(source) {
  const card = source.closest('.vehicle-card, .vehicle');
  const frame = card && card.querySelector('.featured-image, .vehicle__image');
  const primary = frame && frame.querySelector('.vehicle-image, .vehicle__default-image');

  // Remove the source either way, so calling this again skips the card.
  source.remove();
  if (!primary || primary.closest('.vehicle-gallery')) return;

  let photos = [];
  try {
    photos = JSON.parse(source.dataset.photos);
  } catch (e) {
    return;
  }
  photos = (Array.isArray(photos) ? photos : []).slice(0, MAX_PHOTOS - 1);
  if (!photos.length) return;

  // The VDP link: the card or image link, or the image itself when the
  // original card isn't a link (cardStyle.noLink).
  const link = frame.closest('a[href]');
  const href = link ? link.href : primary.href || card.dataset.noLinkTo;
  if (!href) return;

  const total = photos.length + 1;
  const gallery = document.createElement('div');
  gallery.className = 'vehicle-gallery is-first';
  gallery.dataset.total = total;
  gallery.dataset.index = 0;
  gallery.dataset.href = href;

  const track = document.createElement('div');
  track.className = 'vehicle-gallery__track';

  const firstSlide = document.createElement('div');
  firstSlide.className = 'vehicle-gallery__slide is-loaded';
  primary.before(gallery);
  firstSlide.appendChild(primary);
  track.appendChild(firstSlide);

  photos.forEach((src) => {
    // Outside a link the extra slides link to the VDP themselves, like the
    // first image does.
    const slide = document.createElement(link ? 'div' : 'a');
    slide.className = 'vehicle-gallery__slide';
    if (!link) {
      slide.href = href;
      slide.tabIndex = -1;
      slide.setAttribute('aria-hidden', 'true');
    }
    const img = document.createElement('img');
    // Share the original card's image styles (border, rounding, fit).
    if (primary.classList.contains('vehicle-image')) img.className = 'vehicle-image';
    img.alt = primary.getAttribute('alt') || '';
    img.decoding = 'async';
    img.dataset.src = src;
    slide.appendChild(img);
    track.appendChild(slide);
  });

  const dots = Array.from(
    { length: total },
    (_, i) => `<span class="vehicle-gallery__dot${i === 0 ? ' is-active' : ''}"></span>`,
  ).join('');

  gallery.appendChild(track);
  gallery.insertAdjacentHTML(
    'beforeend',
    `<button type="button" class="vehicle-gallery__arrow vehicle-gallery__arrow--prev" aria-label="${LABELS.prev}" disabled>${chevron('left')}</button>
    <button type="button" class="vehicle-gallery__arrow vehicle-gallery__arrow--next" aria-label="${LABELS.next}">
      <span class="vehicle-gallery__view">${LABELS.viewShort}</span>${chevron('right')}
    </button>
    <div class="vehicle-gallery__dots" aria-hidden="true">${dots}</div>`,
  );

  frame.classList.add('has-gallery');
}

export function applyCardGalleries() {
  document.querySelectorAll('.vehicle-gallery-photos').forEach(buildGallery);
}

$(applyCardGalleries);
$(document).on(InventorySearchEvents.POST_RENDER, () => applyCardGalleries());

// Arrows
$(document).on('click', '.vehicle-gallery__arrow', function (e) {
  e.preventDefault();
  e.stopPropagation();

  const gallery = this.closest('.vehicle-gallery');
  const index = parseInt(gallery.dataset.index, 10);
  const isNext = this.classList.contains('vehicle-gallery__arrow--next');

  if (isNext && gallery.classList.contains('is-last')) {
    if (e.metaKey || e.ctrlKey) {
      window.open(gallery.dataset.href, '_blank');
    } else {
      window.location.href = gallery.dataset.href;
    }
    return;
  }

  goTo(gallery, isNext ? index + 1 : index - 1);
});

// Preload the second image as soon as the user shows interest in a card.
$(document).on('mouseenter focusin touchstart', '.vehicle-card, .vehicle', function () {
  const gallery = this.querySelector('.vehicle-gallery');
  if (gallery) loadSlide(gallery, 1);
});

// Swipe (touch devices)
let swipe = null;

$(document).on('touchstart', '.vehicle-gallery', function (e) {
  if (e.originalEvent.touches.length > 1) return;
  const touch = e.originalEvent.touches[0];
  swipe = { gallery: this, x: touch.clientX, y: touch.clientY, dx: 0, horizontal: null };
});

$(document).on('touchmove', '.vehicle-gallery', function (e) {
  if (!swipe || swipe.gallery !== this) return;
  const touch = e.originalEvent.touches[0];
  const dx = touch.clientX - swipe.x;
  const dy = touch.clientY - swipe.y;

  if (swipe.horizontal === null && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
    swipe.horizontal = Math.abs(dx) > Math.abs(dy);
    if (swipe.horizontal) this.classList.add('is-dragging');
  }
  if (!swipe.horizontal) return;
  markInteracting(this);

  // Resist when dragging past the first or last image.
  const atEdge =
    (dx > 0 && this.classList.contains('is-first')) ||
    (dx < 0 && this.classList.contains('is-last'));
  swipe.dx = atEdge ? dx / 3 : dx;
  this.style.setProperty('--gallery-drag', `${swipe.dx}px`);
});

$(document).on('touchend touchcancel', '.vehicle-gallery', function () {
  if (!swipe || swipe.gallery !== this) return;
  const { dx, horizontal } = swipe;
  swipe = null;

  this.classList.remove('is-dragging');
  this.style.setProperty('--gallery-drag', '0px');
  if (!horizontal) return;

  // Swallow the click that may follow a swipe so it doesn't open the VDP.
  this.dataset.swiped = '1';
  setTimeout(() => delete this.dataset.swiped, 300);

  if (Math.abs(dx) > SWIPE_THRESHOLD) {
    const index = parseInt(this.dataset.index, 10);
    goTo(this, dx < 0 ? index + 1 : index - 1);
  }
});

$(document).on('click', '.vehicle-gallery', function (e) {
  if (this.dataset.swiped) {
    e.preventDefault();
    e.stopPropagation();
  }
});
